import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {DropdownBoxCoordinator} from '../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {SameWidthButton} from '../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {Dialog} from '../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogButtonGroup} from '../../../../libs/shared/ui/src/components/dialogs/dialogButtonGroup';
import {DialogHeader} from '../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ApplicationModel} from '../../../models/api/applicationModel';
import {TopicModel} from '../../../models/api/topicModel';
import {ApplicationIcon} from '../../common/applications/applicationIcon';

const StyledDialogButtonGroupDiv = styled.div`
  padding-top: 20px;
`;

const StyledDialogContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 20px 8px 20px;
  line-height: 22px;
`;

const StyledApplicationIconDiv = styled.div`
  display: flex;
  flex-shrink: 0;
  padding-left: 10px;
`;

const TopicLineDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledTopicNameDiv = styled.div`
  padding-left: 8px;
`;

interface ChangeTopicDialogProps {
  application: ApplicationModel;
  selectedTopic: TopicModel;
  applicationObjectTopics: ReadonlyArray<TopicModel>;
  onTopicIdSelect: (id: number) => void;
  onRequestClose: () => void;
}

export const SelectTopicDialog: FC<ChangeTopicDialogProps> = (props) => {
  const {selectedTopic, application, applicationObjectTopics, onTopicIdSelect, onRequestClose} = props;

  const [currentSelection, setCurrentSelection] = useState(selectedTopic);

  function renderValue() {
    return renderTopicLine(currentSelection);
  }

  function renderTopicLine(topic?: TopicModel) {
    return (
      <TopicLineDiv>
        <StyledApplicationIconDiv>
          <ApplicationIcon applicationId={application.id} />
        </StyledApplicationIconDiv>
        <StyledTopicNameDiv> {topic?.name}</StyledTopicNameDiv>
      </TopicLineDiv>
    );
  }

  function renderDropdown(modalProps: PopoverTransferProps) {
    return (
      <Dropdown {...modalProps}>
        <DropdownList {...modalProps}>{renderItems()}</DropdownList>
      </Dropdown>
    );
  }

  function renderItems() {
    return applicationObjectTopics.map((topic) => (
      <DropdownItem key={topic.id} onSelect={() => setCurrentSelection(topic)}>
        <DropdownItemContent>{renderTopicLine(topic)}</DropdownItemContent>
      </DropdownItem>
    ));
  }

  return (
    <Dialog>
      <DialogHeader
        title={
          <Trans id="change-topic-dialog-cancel-select-application-object">Select application object</Trans>
        }
      />
      <StyledDialogContentDiv>
        <Trans id="change-topic-dialog-body-text">
          This message uses variables that reference an application object. Select which one to use:
        </Trans>
        <DropdownBoxCoordinator
          value={renderValue()}
          renderDropdown={(popoverProps) => renderDropdown(popoverProps)}
        />
      </StyledDialogContentDiv>

      <StyledDialogButtonGroupDiv>
        <DialogButtonGroup>
          <ButtonGroupSpacer />
          <SameWidthButton onClick={onRequestClose}>
            <Trans id="change-topic-dialog-cancel">Cancel</Trans>
          </SameWidthButton>
          <SameWidthButton
            variant={ButtonVariantsEnum.PRIMARY}
            onClick={() => {
              onRequestClose();
              if (currentSelection) {
                onTopicIdSelect(currentSelection.id);
              }
            }}
          >
            <Trans id="change-topic-dialog-continue">Continue</Trans>
          </SameWidthButton>
        </DialogButtonGroup>
      </StyledDialogButtonGroupDiv>
    </Dialog>
  );
};
